import React, { useState } from 'react';
import { noop } from 'lodash';

import type { AiAction } from '~/components/AiChat/types';
import type { ActionCardModel } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useActionCard';
import { DismissWithReasonDialog } from '~/components/Clive/CliveDialog/DismissWithReasonDialog';

import { usePendingActions } from './PendingActions';

interface ActionGesturesProps {
  action: AiAction;
  /** The caller's `useActionCard`, so a card runs the hook once for its body and its gestures. */
  model: ActionCardModel;
}

/**
 * Execute and Reject on a waiting action, through the Clive Insights card's own footer and dialog.
 * A gesture in flight from the other pane disables these too, so one action cannot be sent twice.
 */
const ActionGestures: React.FC<ActionGesturesProps> = ({ action, model }) => {
  const { ActionFooterComponent, executeAction, dismissAction, isDone, taskType, title, relatedExposureLabel } = model;
  const { isPending, track } = usePendingActions();
  const [isRejectOpen, setIsRejectOpen] = useState(false);

  if (!ActionFooterComponent) return null;

  // The footer does not await this; the hook has already reported and reverted a failure.
  const handleExecute = (): void => {
    track(action.action_id, executeAction()).catch(noop);
  };

  // A failure rethrows into the dialog, which stays open to retry with the note still written.
  const handleReject = async (reason: string): Promise<void> => {
    await track(action.action_id, dismissAction(reason));
    setIsRejectOpen(false);
  };

  return (
    <>
      <ActionFooterComponent
        action={action}
        onExecute={handleExecute}
        onDismiss={() => setIsRejectOpen(true)}
        isDone={isDone || isPending(action.action_id)}
        type={taskType}
        title={title}
        subtitle={relatedExposureLabel}
        dismissLabel="Reject"
      />

      {isRejectOpen ? (
        <DismissWithReasonDialog
          title={title}
          subtitle={relatedExposureLabel}
          subject="recommendation"
          confirmLabel="Reject"
          onConfirm={handleReject}
          onClose={() => setIsRejectOpen(false)}
        />
      ) : null}
    </>
  );
};

export default ActionGestures;
